import api from './client'

/**
 * The simple Workbench's journey (backend `routers/builder_journey.py`).
 * ONE resolver answers "what is the next thing to do"; the Next step tab, the
 * strip on every other tab and the dock's Review card all read it.
 */

export type StepState = 'done' | 'current' | 'todo'

export interface JourneyAction {
  label: string
  /** Closed vocabulary — see lib/journeyActions.ts. */
  action: string
}

export interface JourneyTab {
  id: string
  label: string
  /** Which component renders it: next | plan | objects | analytics | hub | people | golive. */
  view: string
  /** `objects` views: the object types the tree shows (null = all). */
  types?: string[] | null
  noun?: string
  nouns?: string
  add_type?: string
  intro?: string
  /** `hub` views: Advanced item ids or `tab:<id>`. */
  items?: string[]
  /** A conditional tab's rule; the server only lists the tab while it holds. */
  when?: string
}

export interface JourneyStep {
  id: string
  label: string
  state: StepState
  skipped: boolean
}

export interface JourneyNext {
  id: string
  stage: string | null
  card: string | null
  eyebrow: string
  title: string
  body: string
  meta: string
  primary: JourneyAction | null
  secondary: JourneyAction | null
  skip: { label: string; step: string } | null
  progress: number | null
}

export interface JourneyCheck {
  id: string
  label: string
  hint: string
  optional: boolean
  done: boolean
  action: string
}

export type ObjectStatus = 'planned' | 'building' | 'failed' | 'to_check' | 'checked'

/** The latest automatic check of one screen (a UI test run). */
export interface ScreenTestResult {
  /** `fixed` — every case that failed passes again when re-run on its own,
   *  but the screen has not been tested as a whole since (not Checked). */
  status: 'running' | 'passed' | 'failed' | 'fixed' | 'not_tested' | 'error'
  run_id?: string
  at?: number
  passed?: number
  failed?: number
  total?: number
  message?: string
  case?: string | null
  /** Cases whose newest result comes from a later run: case id → run id. */
  case_runs?: Record<string, string>
}

export interface JourneyObject {
  obj_type: string
  obj_name: string
  title: string
  nav_id: string | null
  built: boolean
  status: ObjectStatus
  fingerprint: string
  last_test?: ScreenTestResult
  /** Who / which run vouched for it, when it is checked. */
  check?: { method?: 'auto' | 'manual'; by?: string; at?: number; run_id?: string; note?: string }
}

export interface CheckLogEntry {
  at: number
  obj: string
  method: 'auto' | 'manual'
  result: string
  by?: string
  run_id?: string
  message?: string
  passed?: number
  failed?: number
}

export interface AppTypeInfo {
  id: string
  label: string
  line: string
}

export interface Journey {
  app_type: string
  app_types: AppTypeInfo[]
  tabs: JourneyTab[]
  /** Every tab defined in tabs.json (a hub may link to one its journey doesn't list). */
  tab_catalog: JourneyTab[]
  steps: JourneyStep[]
  next: JourneyNext
  checks: JourneyCheck[]
  go_live_ready: boolean
  live: boolean
  live_at: number | null
  summary: string
  objects: JourneyObject[]
  counts: {
    planned: number; built: number; pending: number; failed: number
    checked: number; to_check: number; all_checked: boolean; pct: number
  }
  agent: { status: string; asking: boolean; awaiting_review: boolean; current: string; has_plan: boolean }
  people: { count?: number; members?: number; orgs?: number; invited?: number }
  domain: { ready?: boolean; address?: string; url?: string; hostname?: string; own_address?: boolean }
  data: { sample_loaded?: boolean; records?: number }
  skipped: string[]
  plan_approved_at: number | null
  /** An automatic check run still going (its id), else null. */
  tests_running: string | null
  /** The newest outcomes first: automatic runs and people's confirmations. */
  check_log: CheckLogEntry[]
}

export interface PlanRow {
  title: string
  line: string
  tag: string
  heading?: boolean
  obj_name?: string
  nav_id?: string
}

export interface PlanSection {
  id: string
  title: string
  text: string
  rows: PlanRow[]
  count: number
  change: string
}

export interface PlanDocument {
  has_spec: boolean
  sections: PlanSection[]
  markdown: string
  hash: string
  approved_at: number | null
  approved_by: string
  changed_since_approval: boolean
}

const base = (project: string) => `/workbench/${encodeURIComponent(project)}/journey`

export async function getJourney(project: string): Promise<Journey> {
  const { data } = await api.get<Journey>(base(project))
  return data
}

export async function getPlan(project: string): Promise<PlanDocument> {
  const { data } = await api.get<PlanDocument>(`${base(project)}/plan`)
  return data
}

export async function getPlanHistory(project: string): Promise<{ commits: Array<{ hash: string; short_hash: string; author: string; date: string; subject: string }> }> {
  const { data } = await api.get(`${base(project)}/plan/history`)
  return data
}

export async function getAppTypes(): Promise<{ default: string; types: AppTypeInfo[] }> {
  const { data } = await api.get('/workbench/journey/app-types')
  return data
}

export async function skipStep(project: string, step: string, skipped = true): Promise<void> {
  await api.post(`${base(project)}/skip`, { step, skipped })
}

export async function setChecked(project: string, objType: string, objName: string, checked: boolean,
  note?: string): Promise<void> {
  await api.post(`${base(project)}/check`, { obj_type: objType, obj_name: objName, checked, note })
}

/** Check screens automatically with the Testing tool (headless browser).
 *  `objects` = [[type, name]]; empty = every built screen not checked yet. */
export async function testScreens(project: string, objects: Array<[string, string]> = []):
  Promise<{ run_id: string; cases: number; objects: string[]; untested: string[] }> {
  const { data } = await api.post(`${base(project)}/test-screens`, { objects })
  return data
}

export async function approvePlan(project: string): Promise<void> {
  await api.post(`${base(project)}/approve-plan`)
}

export async function setLive(project: string, body: { live: boolean; notify?: boolean; message?: string }): Promise<{ notified: number; notify_failed: string[] }> {
  const { data } = await api.post(`${base(project)}/go-live`, body)
  return data
}

export interface SeedRemoveReport {
  ok: boolean
  deleted: number
  kept: number
  failed: number
  objects: Array<{ obj_name: string; deleted: number; kept: number; failed: number; error?: string; note?: string }>
}

/** Delete the records Sample_Data/seed.json created, newest first, through the engine. */
export async function removeSampleData(project: string): Promise<SeedRemoveReport> {
  const { data } = await api.post<SeedRemoveReport>('/livepreview/seed/remove', { project })
  return data
}

export interface SeedLoadReport {
  ok: boolean
  created: number
  skipped: number
  failed: number
}

export async function loadSampleData(project: string): Promise<SeedLoadReport> {
  const { data } = await api.post<SeedLoadReport>('/livepreview/seed', { project })
  return data
}
